import { FOOTER_COLUMNS, FOOTER_LEGAL_LINKS, type FooterLink } from "@/app/(public)/shared/_logic/footerLinks";
import nowadaysLogo from "@/app/(public)/shared/assets/nowadayslogo.png";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/spacing.css";
import "@/app/(public)/shared/tokens/typography.css";
import Image from "next/image";
import Link from "next/link";
import { FooterDog } from "./FooterDog";

const LINK_CLASS = "text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200";
const BADGE_CLASS = "opacity-60 hover:opacity-100 transition-opacity";
const NEW_TAB = { target: "_blank", rel: "noopener noreferrer" } as const;

const FooterAnchor = ({ link }: { link: FooterLink }) => (
  <Link href={link.href} className={LINK_CLASS} {...(link.href.startsWith("http") ? NEW_TAB : {})}>
    {link.label}
  </Link>
);

/**
 * Public site footer: brand and link columns, a bottom bar, and the Nowadays dog
 * peeking up from the bottom edge.
 *
 * @returns The footer.
 */
export const Footer = () => {
  return (
    <footer className="w-full flex flex-col items-center gap-10 border-t border-landing-primary bg-landing-primary">
      <div className="container-page pt-16 flex flex-col gap-12">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Brand */}
          <div className="flex flex-col gap-4 lg:w-[320px] shrink-0">
            <Image src={nowadaysLogo} width={124} alt="Nowadays" />
            <p className="text-body-sm text-landing-secondary">Enterprise-grade event management platform.</p>
            <a href="mailto:events@nowadays.ai" className={LINK_CLASS}>
              events@nowadays.ai
            </a>
            <div className="flex items-center gap-4 pt-2">
              <Link href="https://www.iata.org/" className={BADGE_CLASS} {...NEW_TAB}>
                <Image src="/landing/iata.svg" width={96} height={32} alt="IATA Accredited Agent" />
              </Link>
              <Link href="https://www.wbenc.org/" className={BADGE_CLASS} {...NEW_TAB}>
                <Image
                  src="/landing/wbenc-certified.webp"
                  width={56}
                  height={32}
                  alt="WBENC Certified Women's Business Enterprise"
                />
              </Link>
            </div>
          </div>

          {/* Link columns */}
          <nav aria-label="Footer" className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10 flex-1">
            {FOOTER_COLUMNS.map((column) => (
              <div key={column.heading} className="flex flex-col gap-4">
                <h2 className="text-xs font-semibold uppercase tracking-[0.08em] text-landing-primary">
                  {column.heading}
                </h2>
                <ul className="flex flex-col gap-3 list-none">
                  {column.links.map((link) => (
                    <li key={link.href}>
                      <FooterAnchor link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pt-6 border-t border-landing-primary">
          <div className="flex items-center gap-6">
            <span className="text-tag-sm text-landing-secondary opacity-70">
              © {new Date().getFullYear()} Nowadays AI Corp.
            </span>
            {FOOTER_LEGAL_LINKS.map((link) => (
              <FooterAnchor key={link.href} link={link} />
            ))}
          </div>
          <div className="flex items-center gap-4">
            <Link href="https://elevenlabs.io/text-to-speech" className={BADGE_CLASS} {...NEW_TAB}>
              <Image
                src="https://eleven-public-cdn.elevenlabs.io/payloadcms/pwsc4vchsqt-ElevenLabsGrants.webp"
                alt="Text to Speech"
                width={120}
                height={120}
              />
            </Link>
            <Link
              href="https://techcrunch.com/2024/12/06/y-combinator-alum-nowadays-founded-by-sisters-raises-2m-to-automate-event-planning/"
              className={BADGE_CLASS}
              {...NEW_TAB}
            >
              <Image src="/landing/techcrunch_button.svg" width={24} height={24} alt="TechCrunch Feature" />
            </Link>
            <Link href="https://www.linkedin.com/company/getnowadays/" className={BADGE_CLASS} {...NEW_TAB}>
              <Image src="/landing/linked_in_button.svg" width={24} height={24} alt="LinkedIn" />
            </Link>
          </div>
        </div>
      </div>
      <FooterDog />
    </footer>
  );
};
